import { useTranslation } from 'react-i18next';
import { FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/data/EmptyState';
import { ApiError } from '@/api/errors';
import { useApiError } from '@/lib/useApiError';
import { useSyncCopyCatalog } from '@/queries/copy';

/**
 * First-use state for a catalog nobody has synced yet.
 *
 * Distinct from "no keys match these filters" on purpose: an empty catalog is
 * not a search problem, and telling an editor to clear a filter they never set
 * reads as "this screen is broken". The catalog is only ever populated by a
 * manifest sync (normally CI after a site deploy), so the recovery is offered
 * right here — `POST /admin/v1/site-copy/sync` with `{}` reads the API's own
 * configured site, and on an empty catalog the orphan guard cannot fire.
 */
export function CatalogEmptyState() {
  const { t } = useTranslation('copy');
  const resolveError = useApiError();
  const sync = useSyncCopyCatalog();

  const failure = sync.error ? resolveError(sync.error) : null;
  // The localized headline drops the server's diagnostic (the resolved URL and
  // why it failed), which is exactly what whoever runs deploys needs to see.
  const detail =
    sync.error instanceof ApiError && sync.error.message && sync.error.message !== failure?.message
      ? sync.error.message
      : null;

  function runSync() {
    sync.mutate(
      {},
      {
        onSuccess: (report) => toast.success(t('catalogEmpty.synced', { keys: report.keys })),
      }
    );
  }

  return (
    <EmptyState
      className="max-w-xl"
      icon={
        <span className="grid size-12 place-items-center rounded-[var(--radius-lg)] bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)]">
          <FileText className="size-6" />
        </span>
      }
      title={t('catalogEmpty.title')}
      body={t('catalogEmpty.body')}
      action={
        <div className="flex flex-col items-center gap-3">
          <Button onClick={runSync} loading={sync.isPending} className="gap-2">
            {sync.isPending ? null : <RefreshCw className="size-4" aria-hidden />}
            {t('catalogEmpty.action')}
          </Button>
          <p className="text-xs text-[color:var(--color-fg-muted)]">{t('catalogEmpty.auto')}</p>
          {failure ? (
            <Alert
              variant="danger"
              title={t('catalogEmpty.failedTitle')}
              className="w-full text-start"
            >
              <p>{failure.message}</p>
              {detail ? (
                <p
                  dir="ltr"
                  className="mt-1 font-mono text-xs [overflow-wrap:anywhere] text-[color:var(--color-fg-muted)]"
                >
                  {detail}
                </p>
              ) : null}
              <p className="mt-2 text-xs text-[color:var(--color-fg-muted)]">
                {t('catalogEmpty.failedHint')}
              </p>
            </Alert>
          ) : null}
        </div>
      }
    />
  );
}
